import { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { 
    Plus, Edit2, Trash2, Check, Star, Users, Building2 
} from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

const defaultPlan = {
    name: '',
    description: '',
    account_type: 'individual',
    pricing_mode: 'subscription',
    currency: 'SAR',
    price_monthly: 0,
    price_yearly: 0,
    price_per_report: 0,
    overage_price: 0,
    features: [],
    limits: { reports: 10, ai_chats: 50, storage_mb: 100 },
    is_active: true,
    is_visible: true,
    is_featured: false,
    trial_enabled: false,
    trial_days: 0,
    trial_activation: 'auto',
    requires_approval: false,
    max_uses_per_user: 0,
    sort_order: 0
};

const AdminPlansPage = () => {
    const { api, isSuperAdmin } = useAdmin();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [editingPlan, setEditingPlan] = useState(null);
    const [planToDelete, setPlanToDelete] = useState(null);
    const [typeFilter, setTypeFilter] = useState('all');
    const [formData, setFormData] = useState(defaultPlan);
    const [featureInput, setFeatureInput] = useState('');

    const fetchPlans = useCallback(async () => {
        try {
            const response = await api.get('/admin/plans');
            setPlans(response.data);
        } catch (error) {
            toast.error('Failed to fetch plans');
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        fetchPlans();
    }, [fetchPlans]);

    const openCreateDialog = () => {
        setEditingPlan(null);
        setFormData(defaultPlan);
        setDialogOpen(true);
    };

    const openEditDialog = (plan) => {
        setEditingPlan(plan);
        setFormData({
            ...plan,
            features: plan.features || [],
            limits: plan.limits || { reports: 10, ai_chats: 50, storage_mb: 100 },
            max_uses_per_user: plan.max_uses_per_user || 0,
            free_uses_per_ip: plan.free_uses_per_ip ?? 1
        });
        setDialogOpen(true);
    };

    const handleAddFeature = () => {
        if (featureInput.trim()) {
            setFormData(prev => ({
                ...prev,
                features: [...prev.features, featureInput.trim()]
            }));
            setFeatureInput('');
        }
    };

    const handleRemoveFeature = (index) => {
        setFormData(prev => ({
            ...prev,
            features: prev.features.filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = async () => {
        if (!formData.name || !formData.description) {
            toast.error('Please fill in all required fields');
            return;
        }

        try {
            if (editingPlan) {
                await api.put(`/admin/plans/${editingPlan.id}`, formData);
                toast.success('Plan updated successfully');
            } else {
                await api.post('/admin/plans', formData);
                toast.success('Plan created successfully');
            }
            setDialogOpen(false);
            fetchPlans();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save plan');
        }
    };

    const handleDelete = async () => {
        if (!planToDelete) return;
        
        try {
            await api.delete(`/admin/plans/${planToDelete.id}`);
            toast.success('Plan deleted successfully');
            setDeleteDialogOpen(false);
            setPlanToDelete(null);
            fetchPlans();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete plan');
        }
    };

    const confirmDelete = (plan) => {
        setPlanToDelete(plan);
        setDeleteDialogOpen(true);
    };

    const filteredPlans = typeFilter === 'all' ? plans : plans.filter(p => p.account_type === typeFilter);

    return (
        <div className="space-y-6" data-testid="admin-plans-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white">Subscription Plans</h1>
                    <p className="text-slate-400 mt-1">Manage pricing plans and features</p>
                </div>
                {isSuperAdmin && (
                    <Button onClick={openCreateDialog} className="bg-teal-500 hover:bg-teal-600" data-testid="create-plan-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Create Plan
                    </Button>
                )}
            </div>

            {/* Account-type filter */}
            <div className="flex flex-wrap items-center gap-2" data-testid="plans-type-filter">
                {[
                    { key: 'all', label: 'All Plans (الكل)', icon: null },
                    { key: 'individual', label: 'Individual (الأفراد)', icon: Users },
                    { key: 'laboratory', label: 'Laboratory (المختبرات)', icon: Building2 },
                    { key: 'guest', label: 'Guest (الزوار)', icon: null },
                ].map(({ key, label, icon: Icon }) => {
                    const count = key === 'all' ? plans.length : plans.filter(p => p.account_type === key).length;
                    return (
                        <Button
                            key={key}
                            variant="outline"
                            size="sm"
                            onClick={() => setTypeFilter(key)}
                            data-testid={`plan-filter-${key}`}
                            className={typeFilter === key
                                ? 'bg-teal-500/20 border-teal-500 text-teal-300 hover:bg-teal-500/30 hover:text-teal-200'
                                : 'bg-slate-800/50 border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white'}
                        >
                            {Icon && <Icon className="w-4 h-4 mr-1.5" />}
                            {label}
                            <Badge className="ml-2 bg-slate-700 text-slate-300 border-0 px-1.5">{count}</Badge>
                        </Button>
                    );
                })}
            </div>

            {/* Plans Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {loading ? (
                    <div className="col-span-full text-center py-12 text-slate-400">
                        Loading plans...
                    </div>
                ) : filteredPlans.length === 0 ? (
                    <div className="col-span-full text-center py-12">
                        <p className="text-slate-400 mb-4">No subscription plans yet</p>
                        {isSuperAdmin && (
                            <Button onClick={openCreateDialog} className="bg-teal-500 hover:bg-teal-600">
                                <Plus className="w-4 h-4 mr-2" />
                                Create First Plan
                            </Button>
                        )}
                    </div>
                ) : (
                    filteredPlans.map((plan) => (
                        <Card 
                            key={plan.id} 
                            className={`border-slate-700 bg-slate-800/50 relative ${plan.is_featured ? 'ring-2 ring-teal-500' : ''}`}
                            data-testid={`plan-card-${plan.id}`}
                        >
                            {plan.is_featured && (
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                    <Badge className="bg-teal-500 text-white">
                                        <Star className="w-3 h-3 mr-1" />
                                        Featured
                                    </Badge>
                                </div>
                            )}
                            <CardHeader className="pb-3">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <CardTitle className="text-lg text-white flex items-center gap-2 flex-wrap">
                                            {plan.name}
                                            {!plan.is_active && (
                                                <Badge variant="outline" className="border-red-500/50 text-red-400 text-xs">
                                                    Inactive
                                                </Badge>
                                            )}
                                            {plan.is_active && plan.is_visible === false && (
                                                <Badge variant="outline" className="border-amber-500/50 text-amber-400 text-xs">
                                                    Hidden
                                                </Badge>
                                            )}
                                        </CardTitle>
                                        <Badge 
                                            variant="outline" 
                                            className={`mt-2 ${plan.account_type === 'laboratory' 
                                                ? 'border-teal-500/50 text-teal-400' 
                                                : plan.account_type === 'guest'
                                                ? 'border-amber-500/50 text-amber-400'
                                                : 'border-slate-600 text-slate-300'}`}
                                        >
                                            {plan.account_type === 'laboratory' ? (
                                                <><Building2 className="w-3 h-3 mr-1" /> Laboratory</>
                                            ) : plan.account_type === 'guest' ? (
                                                <>Guest (زوار)</>
                                            ) : (
                                                <><Users className="w-3 h-3 mr-1" /> Individual</>
                                            )}
                                        </Badge>
                                    </div>
                                    {isSuperAdmin && (
                                        <div className="flex gap-1">
                                            <Button 
                                                variant="ghost" 
                                                size="sm" 
                                                className="h-8 w-8 p-0 text-slate-400 hover:text-white"
                                                onClick={() => openEditDialog(plan)}
                                                data-testid={`edit-plan-${plan.id}`}
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </Button>
                                            <Button 
                                                variant="ghost" 
                                                size="sm" 
                                                className="h-8 w-8 p-0 text-slate-400 hover:text-red-400"
                                                onClick={() => confirmDelete(plan)}
                                                data-testid={`delete-plan-${plan.id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <p className="text-sm text-slate-400">{plan.description}</p>

                                {plan.pricing_mode === 'pay_per_use' ? (
                                    <div>
                                        <div className="flex items-baseline gap-1">
                                            <span className="text-3xl font-bold text-white">{plan.price_per_report}</span>
                                            <span className="text-slate-400">ر.س</span>
                                        </div>
                                        <p className="text-sm text-slate-500">
                                            دفع مقابل {plan.limits?.reports || 1} تقرير
                                        </p>
                                    </div>
                                ) : plan.pricing_mode === 'free' || (!plan.price_monthly && !plan.price_yearly) ? (
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-3xl font-bold text-white">مجاني</span>
                                    </div>
                                ) : (
                                    <div>
                                        <div className="flex items-baseline gap-1">
                                            <span className="text-3xl font-bold text-white">{plan.price_monthly}</span>
                                            <span className="text-slate-400">ر.س/شهر</span>
                                        </div>
                                        {plan.price_yearly > 0 && (
                                            <p className="text-sm text-slate-500">{plan.price_yearly} ر.س/سنة</p>
                                        )}
                                        {plan.overage_price > 0 && (
                                            <p className="text-xs text-slate-500">تجاوز: {plan.overage_price} ر.س/تقرير</p>
                                        )}
                                    </div>
                                )}

                                <div className="border-t border-slate-700 pt-4">
                                    <p className="text-sm font-medium text-slate-300 mb-2">Features:</p>
                                    <ul className="space-y-2">
                                        {(plan.features || []).slice(0, 5).map((feature, i) => (
                                            <li key={i} className="flex items-start gap-2 text-sm text-slate-400">
                                                <Check className="w-4 h-4 text-teal-400 flex-shrink-0 mt-0.5" />
                                                {feature}
                                            </li>
                                        ))}
                                        {plan.features?.length > 5 && (
                                            <li className="text-sm text-slate-500">+{plan.features.length - 5} more features</li>
                                        )}
                                    </ul>
                                </div>

                                {plan.limits && (
                                    <div className="border-t border-slate-700 pt-4">
                                        <p className="text-sm font-medium text-slate-300 mb-2">Limits:</p>
                                        <div className="grid grid-cols-3 gap-2 text-center">
                                            <div className="bg-slate-700/50 rounded-lg p-2">
                                                <p className="text-lg font-bold text-white">{plan.limits.reports || '∞'}</p>
                                                <p className="text-xs text-slate-400">Reports</p>
                                            </div>
                                            <div className="bg-slate-700/50 rounded-lg p-2">
                                                <p className="text-lg font-bold text-white">{plan.limits.ai_chats || '∞'}</p>
                                                <p className="text-xs text-slate-400">AI Chats</p>
                                            </div>
                                            <div className="bg-slate-700/50 rounded-lg p-2">
                                                <p className="text-lg font-bold text-white">{plan.limits.storage_mb || '∞'}</p>
                                                <p className="text-xs text-slate-400">MB</p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    ))
                )}
            </div>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingPlan ? 'Edit Plan' : 'Create Plan'}</DialogTitle>
                        <DialogDescription className="text-slate-400">
                            {editingPlan ? 'Update the subscription plan details' : 'Create a new subscription plan'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-slate-300">Plan Name *</Label>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="e.g., Professional"
                                    className="bg-slate-700/50 border-slate-600 text-white"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-slate-300">Account Type</Label>
                                <Select 
                                    value={formData.account_type} 
                                    onValueChange={(v) => setFormData(prev => ({ ...prev, account_type: v }))}
                                >
                                    <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-slate-800 border-slate-700">
                                        <SelectItem value="individual" className="text-white">Individual</SelectItem>
                                        <SelectItem value="laboratory" className="text-white">Laboratory</SelectItem>
                                        <SelectItem value="guest" className="text-white">Guest (زوار بدون تسجيل)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-slate-300">Pricing Mode</Label>
                                <Select 
                                    value={formData.pricing_mode} 
                                    onValueChange={(v) => setFormData(prev => ({ ...prev, pricing_mode: v }))}
                                >
                                    <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white" data-testid="pricing-mode-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-slate-800 border-slate-700">
                                        <SelectItem value="subscription" className="text-white">Subscription (شهري/سنوي)</SelectItem>
                                        <SelectItem value="pay_per_use" className="text-white">Pay-per-use (دفع لكل تقرير)</SelectItem>
                                        <SelectItem value="free" className="text-white">Free (مجاني)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-slate-300">Currency</Label>
                                <Input
                                    value={formData.currency || 'SAR'}
                                    disabled
                                    className="bg-slate-700/30 border-slate-600 text-slate-300"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-slate-300">Description *</Label>
                            <Textarea
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                placeholder="Brief description of the plan"
                                className="bg-slate-700/50 border-slate-600 text-white resize-none"
                                rows={2}
                            />
                        </div>

                        {formData.pricing_mode === 'pay_per_use' ? (
                            <div className="space-y-2">
                                <Label className="text-slate-300">Price per purchase (ر.س)</Label>
                                <Input
                                    type="number"
                                    value={formData.price_per_report}
                                    onChange={(e) => setFormData(prev => ({ ...prev, price_per_report: parseFloat(e.target.value) || 0 }))}
                                    className="bg-slate-700/50 border-slate-600 text-white"
                                    data-testid="price-per-report-input"
                                />
                                <p className="text-xs text-slate-500">السعر الإجمالي مقابل عدد التقارير المحدد في الحدود (Reports).</p>
                            </div>
                        ) : formData.pricing_mode === 'free' ? (
                            <p className="text-sm text-slate-400 border border-slate-700 rounded-lg p-3">خطة مجانية — لا يلزم تحديد سعر.</p>
                        ) : (
                            <div className="grid grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-slate-300">Monthly (ر.س)</Label>
                                    <Input
                                        type="number"
                                        value={formData.price_monthly}
                                        onChange={(e) => setFormData(prev => ({ ...prev, price_monthly: parseFloat(e.target.value) || 0 }))}
                                        className="bg-slate-700/50 border-slate-600 text-white"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-slate-300">Yearly (ر.س)</Label>
                                    <Input
                                        type="number"
                                        value={formData.price_yearly}
                                        onChange={(e) => setFormData(prev => ({ ...prev, price_yearly: parseFloat(e.target.value) || 0 }))}
                                        className="bg-slate-700/50 border-slate-600 text-white"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-slate-300">Overage (ر.س)</Label>
                                    <Input
                                        type="number"
                                        value={formData.overage_price}
                                        onChange={(e) => setFormData(prev => ({ ...prev, overage_price: parseFloat(e.target.value) || 0 }))}
                                        className="bg-slate-700/50 border-slate-600 text-white"
                                    />
                                </div>
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label className="text-slate-300">Features</Label>
                            <div className="flex gap-2">
                                <Input
                                    value={featureInput}
                                    onChange={(e) => setFeatureInput(e.target.value)}
                                    placeholder="Add a feature"
                                    className="bg-slate-700/50 border-slate-600 text-white"
                                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFeature())}
                                />
                                <Button type="button" variant="secondary" onClick={handleAddFeature} className="bg-slate-700 hover:bg-slate-600">
                                    Add
                                </Button>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-2">
                                {formData.features.map((feature, i) => (
                                    <Badge key={i} variant="secondary" className="bg-slate-700 text-slate-200 gap-1">
                                        {feature}
                                        <button onClick={() => handleRemoveFeature(i)} className="ml-1 hover:text-red-400">×</button>
                                    </Badge>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-slate-300">Limits</Label>
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <Label className="text-xs text-slate-400">Reports/month</Label>
                                    <Input
                                        type="number"
                                        value={formData.limits.reports}
                                        onChange={(e) => setFormData(prev => ({ 
                                            ...prev, 
                                            limits: { ...prev.limits, reports: parseInt(e.target.value) || 0 }
                                        }))}
                                        className="bg-slate-700/50 border-slate-600 text-white"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs text-slate-400">AI Chats/month</Label>
                                    <Input
                                        type="number"
                                        value={formData.limits.ai_chats}
                                        onChange={(e) => setFormData(prev => ({ 
                                            ...prev, 
                                            limits: { ...prev.limits, ai_chats: parseInt(e.target.value) || 0 }
                                        }))}
                                        className="bg-slate-700/50 border-slate-600 text-white"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs text-slate-400">Storage (MB)</Label>
                                    <Input
                                        type="number"
                                        value={formData.limits.storage_mb}
                                        onChange={(e) => setFormData(prev => ({ 
                                            ...prev, 
                                            limits: { ...prev.limits, storage_mb: parseInt(e.target.value) || 0 }
                                        }))}
                                        className="bg-slate-700/50 border-slate-600 text-white"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2 mt-2">
                                <Label className="text-xs text-slate-400">Max uses per user (عدد مرات استخدام الباقة لكل مستخدم — اختياري، 0 = غير محدود)</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    value={formData.max_uses_per_user}
                                    onChange={(e) => setFormData(prev => ({ ...prev, max_uses_per_user: parseInt(e.target.value) || 0 }))}
                                    className="bg-slate-700/50 border-slate-600 text-white"
                                    data-testid="plan-max-uses-input"
                                />
                                <p className="text-xs text-slate-500">مثال: ضع 1 لباقة مجانية/ترحيبية تُستخدم مرة واحدة فقط لكل مستخدم</p>
                            </div>
                            {formData.account_type === 'guest' && (
                                <div className="space-y-2 mt-2">
                                    <Label className="text-xs text-slate-400">Free analyses per IP (عدد التحاليل المجانية لكل زائر/IP)</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        value={formData.free_uses_per_ip ?? 1}
                                        onChange={(e) => setFormData(prev => ({ ...prev, free_uses_per_ip: parseInt(e.target.value) || 0 }))}
                                        className="bg-slate-700/50 border-slate-600 text-white"
                                        data-testid="plan-free-per-ip-input"
                                    />
                                    <p className="text-xs text-slate-500">بعد استنفاده يُطلب من الزائر الدفع (سعر التقرير أعلاه) أو التسجيل</p>
                                </div>
                            )}
                        </div>

                        <div className="border-t border-slate-700 pt-4 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="flex items-center gap-3">
                                    <Switch
                                        checked={formData.is_active}
                                        onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_active: v }))}
                                        data-testid="plan-active-switch"
                                    />
                                    <Label className="text-slate-300">Active (متاحة)</Label>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Switch
                                        checked={formData.is_visible}
                                        onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_visible: v }))}
                                        data-testid="plan-visible-switch"
                                    />
                                    <Label className="text-slate-300">Visible (ظاهرة للتسجيل)</Label>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Switch
                                        checked={formData.is_featured}
                                        onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_featured: v }))}
                                    />
                                    <Label className="text-slate-300">Featured (مميزة)</Label>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Switch
                                        checked={formData.requires_approval}
                                        onCheckedChange={(v) => setFormData(prev => ({ ...prev, requires_approval: v }))}
                                        data-testid="plan-approval-switch"
                                    />
                                    <Label className="text-slate-300">Manual Approval (موافقة يدوية)</Label>
                                </div>
                            </div>

                            <div className="border-t border-slate-700 pt-4 space-y-3">
                                <div className="flex items-center gap-3">
                                    <Switch
                                        checked={formData.trial_enabled}
                                        onCheckedChange={(v) => setFormData(prev => ({ ...prev, trial_enabled: v }))}
                                        data-testid="plan-trial-switch"
                                    />
                                    <Label className="text-slate-300">Trial enabled (فترة تجريبية)</Label>
                                </div>
                                {formData.trial_enabled && (
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-xs text-slate-400">Trial days (أيام التجربة)</Label>
                                            <Input
                                                type="number"
                                                value={formData.trial_days}
                                                onChange={(e) => setFormData(prev => ({ ...prev, trial_days: parseInt(e.target.value) || 0 }))}
                                                className="bg-slate-700/50 border-slate-600 text-white"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-xs text-slate-400">Activation (التفعيل)</Label>
                                            <Select 
                                                value={formData.trial_activation} 
                                                onValueChange={(v) => setFormData(prev => ({ ...prev, trial_activation: v }))}
                                            >
                                                <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent className="bg-slate-800 border-slate-700">
                                                    <SelectItem value="auto" className="text-white">Auto (تلقائي)</SelectItem>
                                                    <SelectItem value="manual" className="text-white">Manual (يدوي)</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-600 text-slate-300">
                            Cancel
                        </Button>
                        <Button onClick={handleSubmit} className="bg-teal-500 hover:bg-teal-600">
                            {editingPlan ? 'Update Plan' : 'Create Plan'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent className="bg-slate-800 border-slate-700">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">Delete Plan</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">
                            Are you sure you want to delete &quot;{planToDelete?.name}&quot;? This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default AdminPlansPage;
